import { BiasMatchGame } from '../../components/bias';
import type { CognitiveBias } from '../../types';
import { Helmet } from 'react-helmet-async';
import { GradientTitle } from '../../components/ui';

interface MatchPageProps {
  biases: CognitiveBias[];
}

export const MatchPage: React.FC<MatchPageProps> = ({ biases }) => {
  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Match Game - Practice Cognitive Biases</title>
        <meta 
          name="description" 
          content="Challenge yourself with our interactive matching game. Test your knowledge by connecting real-world examples to their corresponding cognitive biases."
        />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/practice/match/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Cognitive Bias Match Game | Interactive Learning" />
        <meta 
          property="og:description" 
          content="Challenge yourself with our interactive matching game. Test your knowledge by connecting real-world examples to their corresponding cognitive biases."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/practice/match/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Cognitive Bias Match Game | Interactive Learning" />
        <meta 
          name="twitter:description" 
          content="Challenge yourself with our interactive matching game. Test your knowledge by connecting real-world examples to their corresponding cognitive biases."
        />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Main heading */}
          <div className="text-center mb-8">
            <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
              Cognitive Bias Match Game
            </GradientTitle>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
              Test your knowledge by matching cognitive biases to either their definitions or real-world examples.
              Use the toggle to switch between study modes.
            </p>
          </div>
          
          <BiasMatchGame biases={biases} />
          
          {/* Explanation Section */}
          <div className="mt-12 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-5 mb-6">
              <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">About the Match Game</h2>
            </div>
            
            <div className="text-gray-700 mb-6">
              The match game helps you improve your understanding of cognitive biases by challenging you to connect biases with either their definitions or examples of how they manifest in real life.
            </div>
            
            <div className="text-gray-700 mb-6">
              <span className="font-medium">Study Mode:</span> Switch between definitions and examples to practice different aspects of cognitive biases. Definitions help you learn the concepts, while examples help you recognize biases in everyday situations.
            </div>
            
            <div className="text-gray-700">
              Regular practice with this match game will improve your ability to recognize cognitive biases when they occur, helping you make more rational decisions.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; 